import React, { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IconButton } from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import ChevronRight from "@material-ui/icons/ChevronRight";
import ExpandMore from "@material-ui/icons/ExpandMore";

import styles from "./style.module.scss";
import IntlMessage from "../../helpers/IntlMessage";
import TagEditDrawer from "./TagEditDrawer";
import { changeAlphaOfColor } from "./colorUtils";

import {
  selectImageTags,
  selectIsFetchingData,
  selectImageId,
} from "redux/image/selectors";
import { selectProjectId } from "redux/app/selectors";
import { selectUserRole } from "redux/permission/selector";
import {
  createTagAndAddTagToSource,
  deleteTagOfSource,
  selectOneSource,
  addTagToSource,
} from "redux/image/actions";

const EDITABLE_ROLES = ["admin", "project_manager"];

function Tag() {
  const dispatch = useDispatch();

  // ─── Redux selectors ───────────────────────────────────
  const imageTags = useSelector(selectImageTags);
  const isFetchingData = useSelector(selectIsFetchingData);
  const projectId = useSelector(selectProjectId);
  const imageId = useSelector(selectImageId);
  const roleCode = useSelector(selectUserRole);

  // ─── Local state ───────────────────────────────────────
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openEditTagPanel, setOpenEditTagPanel] = useState(false);
  const [color, setColor] = useState("");

  const isAllowedToEdit = EDITABLE_ROLES.includes(roleCode);

  // ─── Callbacks ─────────────────────────────────────────
  const refreshSource = useCallback(() => {
    dispatch(selectOneSource({ id: imageId }));
  }, [dispatch, imageId]);

  const handleCollapse = () => {
    setIsCollapsed((prev) => !prev);
  };

  const handleOpenEditTagPanel = (event) => {
    event.stopPropagation();
    setOpenEditTagPanel(true);
  };

  const handleCloseEditTagPanel = () => {
    setOpenEditTagPanel(false);
  };

  const handleRemoveTagFromSource = useCallback(
    (tags) => {
      dispatch(
        deleteTagOfSource({
          payload: {
            projectId,
            sourceIds: [imageId],
            tags: tags.map((item) => item.tagId),
          },
          callback: refreshSource,
        })
      );
    },
    [dispatch, projectId, imageId, refreshSource]
  );

  const handleAddTagToSource = useCallback(
    (tag) => {
      dispatch(
        addTagToSource({
          payload: {
            projectId,
            sourceIds: [imageId],
            tags: [tag.tag_id],
          },
          callback: refreshSource,
        })
      );
    },
    [dispatch, projectId, imageId, refreshSource]
  );

  const handleCreateSourceTag = useCallback(
    (tag) => {
      dispatch(
        createTagAndAddTagToSource({
          payload: {
            projectId,
            newtag: [{ value: tag.name, color }],
            sourceIds: [imageId],
          },
          callback: refreshSource,
        })
      );
    },
    [dispatch, projectId, imageId, color, refreshSource]
  );

  const handleChangeTagAutoComplete = useCallback(
    (value) => {
      if (value.tag_id === -1) {
        handleCreateSourceTag(value);
      } else {
        handleAddTagToSource(value);
      }
    },
    [handleCreateSourceTag, handleAddTagToSource]
  );

  // ─── Render ────────────────────────────────────────────
  return (
    <div className={styles.tagContainer}>
      {/* Header */}
      <div
        className={styles.tagHeader}
        role="button"
        tabIndex="0"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleCollapse();
        }}
        onClick={handleCollapse}
      >
        <div className={styles.headerLeft}>
          <button type="button">
            {isCollapsed ? <ChevronRight /> : <ExpandMore />}
          </button>
          <span>
            <IntlMessage id="common.tag" />
          </span>
        </div>

        {isAllowedToEdit && (
          <IconButton
            size="small"
            className={styles.editButton}
            onClick={handleOpenEditTagPanel}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
      </div>

      {/* Tag List */}
      {imageTags && imageTags.length === 0 && !isCollapsed ? (
        <span className={styles.noData}>
          <IntlMessage id="common.no-data-to-show" />
        </span>
      ) : !isCollapsed ? (
        <div className={styles.tagBody}>
          {(imageTags || []).map((item) => (
            <span
              key={item.tagId}
              className={styles["tag-wrapper"]}
              style={{
                backgroundColor: changeAlphaOfColor(item.color, 0.2),
                color: item.color,
              }}
            >
              {item.tagName}
            </span>
          ))}
        </div>
      ) : null}

      {/* Edit Panel */}
      {isAllowedToEdit && (
        <TagEditDrawer
          open={openEditTagPanel}
          onClose={handleCloseEditTagPanel}
          imageTags={imageTags}
          isFetchingData={isFetchingData}
          onRemoveTag={handleRemoveTagFromSource}
          onChangeTagAutoComplete={handleChangeTagAutoComplete}
          onChangeColor={setColor}
        />
      )}
    </div>
  );
}

export default Tag;
